/** UI strings for the F06 models (Bohr atom, populations, collisions); merged into the photons dictionary. */
export const sv = {
  bohrTitle: "Bohrs atommodell: nivåer, banor och korrespondens",
  bohrNote:
    "Enelektronsystem med reducerad massa. Konstanten C flyttar alla energier lika mycket (val av nollnivå); fotonenergier och våglängder ändras inte. Hastigheten är den relativa farten mellan partiklarna.",
  bohrViewLevels: "Energinivåer och spektrum",
  bohrViewOrbits: "Banor",
  bohrViewCorrespondence: "Korrespondens",
  bohrSystem: "System",
  bohrHydrogen: "H",
  bohrDeuterium: "D",
  bohrInfinite: "H, M → ∞",
  bohrHelium: "He⁺",
  bohrBeryllium: "Be³⁺",
  bohrPositronium: "Positronium",
  bohrMuonic: "Myonväte",
  bohrLevel: "Nivå n",
  bohrJump: "Hopp k i n + k → n",
  bohrUpper: "Övre nivå n_U",
  bohrLower: "Undre nivå n_L",
  bohrProcess: "Process",
  bohrEmission: "Emission",
  bohrAbsorption: "Absorption",
  bohrOffset: "Nollnivåkonstant C (eV)",
  bohrLevelsSvg: "Energinivådiagram och linjespektrum",
  bohrLevelsDesc:
    "Överst nivåerna n = 1 till 12 på en linjär energiaxel, med kontinuet skuggat ovanför jonisationsgränsen och en pil för den valda övergången. Nederst serien som slutar på den undre nivån som streck längs en våglängdsaxel, med den valda linjen markerad och seriegränsen streckad.",
  bohrLevelLabel: "n = {n}",
  bohrContinuum: "kontinuum",
  bohrOldZero: "E = 0 före flytten",
  bohrAxisEnergy: "E + C (eV)",
  bohrAxisLambda: "λ (nm)",
  bohrUpperEnergy: "E(n_U) + C (eV)",
  bohrLowerEnergy: "E(n_L) + C (eV)",
  bohrPhotonEnergy: "Fotonenergi hf = E(n_U) − E(n_L) (eV)",
  bohrWavelength: "Våglängd λ = hc/(hf) (nm)",
  bohrFrequency: "Frekvens f (Hz)",
  bohrSeriesLimit: "Seriegräns för n_L (nm)",
  bohrBinding: "Jonisationsenergi från n_L (eV)",
  bohrReduced: "m_r/m_e",
  bohrRelative: "λ / λ för vanligt väte, samma övergång",
  bohrLevelsFormula: String.raw`$hf = E_U - E_L$ (39.5), $E_n = -hcR_M Z^2/n^2 + C$ med $R_M = (m_r/m_e)R_\infty$ (39.14, 39.15, 39.17). Konstanten $C$ faller bort ur varje skillnad.`,
  bohrOrbitsSvg: "Bohrbanor och stående våg",
  bohrOrbitsDesc:
    "Vänster: banorna n = 1 till n_U i skala, den övre och undre nivåns bana markerade och en stående de Broglie-våg med n_U våglängder längs den övre banan. Höger: staplar för r_n/r(n_U), som växer som n², och |E_n|/|E_1|, som avtar som 1/n².",
  bohrBarsTitle: "r_n/r(n_U) och |E_n|/|E_1|",
  bohrRadius: "Avstånd r(n_U) mellan partiklarna (nm)",
  bohrSpeed: "v/c = Zα/n_U",
  bohrAngular: "L/ħ",
  bohrKinetic: "K = −E (eV), nollnivå i oändligheten",
  bohrPotential: "U = 2E (eV), nollnivå i oändligheten",
  bohrShifted: "E(n_U) + C (eV)",
  bohrDeBroglie: "de Broglie-våglängd 2πr/n_U (nm)",
  bohrRadiusRatio: "r(n_U)/r(n_L)",
  bohrEnergyRatio: "E(n_U)/E(n_L), nollnivå i oändligheten",
  bohrOrbitsFormula: String.raw`$r_n = n^2 a_0 (m_e/m_r)/Z$ (39.8, 39.11), $v_n = Z\alpha c/n$ (39.9), $L = n\hbar$ (39.6), $2\pi r_n = n\lambda$. $K_n = -E_n$ och $U_n = 2E_n$ (39.12–39.14) gäller bara med $E = 0$ vid oändligt avstånd; med nollnivåkonstanten $C$ är $K_n = C - E_n'$ där $E_n' = E_n + C$.`,
  bohrCorrSvg: "Fotonfrekvens mot klassisk omloppsfrekvens",
  bohrCorrDesc:
    "Kvoten f(n + k → n)/(k f_rot(n)) mot n på logaritmisk axel från 1 till 1000. Den streckade vågräta linjen vid 1 är den klassiska förutsägelsen; den heldragna kurvan är det valda hoppet k, den streckade kurvan k = 1 som jämförelse, och punkten markerar valt n.",
  bohrAxisLevel: "n (logaritmisk)",
  bohrClassical: "klassiskt: f = k f_rot",
  bohrRotation: "Omloppsfrekvens f_rot(n) = v/(2πr) (Hz)",
  bohrPhotonFrequency: "Fotonfrekvens f(n + k → n) (Hz)",
  bohrRatio: "f/(k f_rot)",
  bohrRatioGap: "1 − f/(k f_rot)",
  bohrAsymptote: "1 − 3/(2n), gäller för k = 1",
  bohrCorrWavelength: "Fotonens våglängd (m)",
  bohrCorrFormula: String.raw`$f_\text{rot}(n) = v_n/(2\pi r_n) = 2|E_n|/(hn)$ och $f(n+k\to n) = (E_{n+k}-E_n)/h$, så $f/(k f_\text{rot}) = n^3\left[1/n^2 - 1/(n+k)^2\right]/(2k) \to 1$. För $k = 1$: $n(2n+1)/[2(n+1)^2] \approx 1 - 3/(2n)$ (Challenge 39.87).`,

  popTitle: "Populationer: termisk jämvikt och fyrnivålaser",
  popNote:
    "Termisk vy: Boltzmannfördelning per tillstånd, multiplicerad med degenerationsgraden för en hel nivå. Laservy: en förenklad hastighetsmodell för ett stationärt kretslopp under lasertröskeln, utan stimulerad emission.",
  popViewThermal: "Termisk jämvikt",
  popViewLaser: "Fyrnivålaser",
  popGap: "Nivåavstånd ΔE (eV)",
  popTemperature: "Temperatur T (K)",
  popLowerDegeneracy: "Degenerationsgrad g₁ (undre nivå)",
  popUpperDegeneracy: "Degenerationsgrad g₂ (övre nivå)",
  popThermalSvg: "Populationskvot mot kT/ΔE",
  popThermalDesc:
    "Kvoten mellan övre och undre population mot kT/ΔE på logaritmisk axel från 0,01 till 100. Heldragen kurva per tillstånd, e^(−ΔE/kT), som närmar sig 1 underifrån; streckad kurva per nivå, (g₂/g₁) gånger detta. Den vågräta streckade linjen markerar lika population.",
  popAxisX: "kT/ΔE (logaritmisk)",
  popEqual: "lika population",
  popKT: "kT (eV)",
  popExponent: "ΔE/kT",
  popPerState: "Per tillstånd n₂/n₁ = e^(−ΔE/kT)",
  popPerLevel: "Per nivå N₂/N₁ = (g₂/g₁) e^(−ΔE/kT)",
  popUpperShare: "Andel i övre nivån N₂/(N₁ + N₂)",
  popNet: "Nettoabsorption relativt, 1 − n₂/n₁",
  popOutside: "Utanför diagrammets kT/ΔE-intervall",
  popYes: "ja",
  popNo: "nej",
  popThermalFormula: String.raw`Per tillstånd $n_2/n_1 = e^{-\Delta E/kT}$ (39.18), per nivå $N_2/N_1 = (g_2/g_1)\,e^{-\Delta E/kT}$. För $\Delta E > 0$ och varje ändlig $T > 0$ är kvoten per tillstånd mindre än 1: termisk jämvikt ger aldrig populationsinversion. Nettoabsorptionen är proportionell mot $n_1 - n_2$ (lika Einsteinkoefficienter per tillstånd, utanför boken).`,
  popGround: "Grund",
  popE1: "E₁",
  popE2: "E₂",
  popE3: "E₃",
  popPump: "Pumptakt W per atom i grundtillståndet (s⁻¹)",
  popUpperLifetime: "Livslängd τ₂ för E₂ (s)",
  popLowerLifetime: "Livslängd τ₁ för E₁ (s)",
  popLaserSvg: "Fyrnivåschema med populationer",
  popLaserDesc:
    "Fyra nivåer: grundtillstånd, E₁, E₂ och E₃. Pilar visar pumpning från grund till E₃, sönderfall E₃ → E₂, laserövergången E₂ → E₁ och sönderfall E₁ → grund. Staplarna till höger visar varje nivås andel av atomerna på logaritmisk skala från 10⁻¹⁰ till 1.",
  popBarScale: "andel (log, 10⁻¹⁰–1)",
  popTopLifetime: "Livslängd τ₃ för E₃, fast (s)",
  popFraction: "Andel i {level}",
  popInversion: "(N₂ − N₁)/N",
  popInverted: "Populationsinversion E₂ över E₁",
  popFlux: "Flöde genom kretsloppet Φ/N (s⁻¹)",
  popLaserFormula: String.raw`Stationärt kretslopp grund → $E_3$ → $E_2$ → $E_1$ → grund med pumptakt $W$ och livslängder $\tau_3, \tau_2, \tau_1$: samma flöde $\Phi$ genom varje steg, så $N_0 = \Phi/W$ och $N_i = \Phi\tau_i$. Därmed gäller $N_2 > N_1$ precis när $\tau_2 > \tau_1$, oberoende av $W$ (lika degenerationsgrad, under lasertröskeln; förenklad modell utanför boken).`,

  colTitle: "Stötar: Rutherfordspridning och stötexcitation",
  colNote:
    "Rutherford: klassiska banor för en alfapartikel i kärnans coulombfält, kärnan fast. Stötexcitation: icke-relativistisk stöt mot en väteatom i vila i grundtillståndet.",
  colViewRutherford: "Rutherfordspridning",
  colViewThreshold: "Stötexcitation",
  colNucleus: "Kärna",
  colAlphaKinetic: "Alfapartikelns kinetiska energi K (MeV)",
  colImpact: "Stötparameter b (fm)",
  colRutherfordSvg: "Alfapartikelbanor kring en kärna",
  colRutherfordDesc:
    "Kärnan i mitten till höger, ritad med radien 1,2A^(1/3) fm. Alfapartiklar kommer in från vänster. Grå banor för stötparametrar d/4, d/2, d och 2d; den markerade banan har vald stötparameter b. Den streckade cirkeln har radien d, vändavståndet vid frontal stöt.",
  colScale: "10 fm",
  colHeadOnCircle: "r = d",
  colHeadOn: "Vändavstånd frontalt d = 2Zke²/K (fm)",
  colHeadOnRecoil: "d med rekyl, K → KM/(m + M) (fm)",
  colRadius: "Kärnradie R = 1,2A^(1/3) (fm)",
  colAngle: "Spridningsvinkel θ (°)",
  colClosest: "Minsta avstånd r_min (fm)",
  colClosestRatio: "r_min/R",
  colRecoil: "Kärnans rekylenergi om den tar all rörelsemängd (MeV)",
  colRecoilShare: "Rekylenergi i procent av K",
  colRutherfordFormula: String.raw`Frontalt: $K = 2Zke^2/d$, så $d = 2Zke^2/K$ med $ke^2 = 1{,}440$ MeV fm (Example 39.4, fast kärna). Med stötparameter $b$: $\tan(\theta/2) = d/(2b)$ och $r_\text{min} = \tfrac{d}{2}\left(1+\sqrt{1+(2b/d)^2}\right)$. $R \approx 1{,}2A^{1/3}$ fm. Med rekyl ersätts $K$ av $KM/(m+M)$.`,
  colElectron: "Elektron",
  colHydrogen: "Väteatom",
  colAlpha: "Alfapartikel",
  colProjectile: "Projektil",
  colLabKinetic: "Projektilens kinetiska energi K i labbet (eV)",
  colThresholdSvg: "Väteatomens nivåer och tillgänglig energi",
  colThresholdDesc:
    "Väteatomens nivåer n = 1 till 8 räknat från grundnivån, kontinuet skuggat ovanför 13,6 eV. Nivåer som stöten kan nå är markerade. Den fyllda stapeln visar den energi som kan bli excitationsenergi, den streckade projektilens kinetiska energi i labbet.",
  colBarAvailable: "K_rel",
  colBarLab: "K",
  colFactor: "1 + m/M",
  colAvailable: "Tillgänglig K_rel = KM/(m + M) (eV)",
  colCentreOfMass: "Bunden i masscentrums rörelse (eV)",
  colHighest: "Högsta nivå som kan nås",
  colIonizes: "jonisation möjlig",
  colNone: "ingen excitation",
  colThreshold: "Tröskel för n = 2: (1 + m/M)·10,2 eV (eV)",
  colSpeed: "Projektilens fart (m/s)",
  colThresholdSpeed: "Minsta fart för n = 2 (m/s)",
  colLines: "Möjliga emissionslinjer därefter (nm)",
  colThresholdFormula: String.raw`Rörelsemängden bevaras, så masscentrums rörelse behåller $Km/(m+M)$; högst $K_\text{rel} = KM/(m+M)$ kan bli excitationsenergi. Tröskeln är $K_\text{min} = (1+m/M)\,\Delta E$ (icke-relativistiskt, målet i vila). Elektron på väteatom: $1+m/M = 1{,}000544$; väteatom på väteatom: $2$.`,
};

export const en: Record<keyof typeof sv, string> = {
  bohrTitle: "Bohr atom: levels, orbits and correspondence",
  bohrNote:
    "One-electron systems with reduced mass. The constant C shifts every energy by the same amount (choice of energy zero); photon energies and wavelengths do not change. The speed is the relative speed of the pair.",
  bohrViewLevels: "Energy levels and spectrum",
  bohrViewOrbits: "Orbits",
  bohrViewCorrespondence: "Correspondence",
  bohrSystem: "System",
  bohrHydrogen: "H",
  bohrDeuterium: "D",
  bohrInfinite: "H, M → ∞",
  bohrHelium: "He⁺",
  bohrBeryllium: "Be³⁺",
  bohrPositronium: "Positronium",
  bohrMuonic: "Muonic hydrogen",
  bohrLevel: "Level n",
  bohrJump: "Jump k in n + k → n",
  bohrUpper: "Upper level n_U",
  bohrLower: "Lower level n_L",
  bohrProcess: "Process",
  bohrEmission: "Emission",
  bohrAbsorption: "Absorption",
  bohrOffset: "Energy-zero constant C (eV)",
  bohrLevelsSvg: "Energy-level diagram and line spectrum",
  bohrLevelsDesc:
    "Top: levels n = 1 to 12 on a linear energy axis, the continuum shaded above the ionization limit and an arrow for the chosen transition. Bottom: the series ending on the lower level as ticks along a wavelength axis, with the chosen line marked and the series limit dashed.",
  bohrLevelLabel: "n = {n}",
  bohrContinuum: "continuum",
  bohrOldZero: "E = 0 before the shift",
  bohrAxisEnergy: "E + C (eV)",
  bohrAxisLambda: "λ (nm)",
  bohrUpperEnergy: "E(n_U) + C (eV)",
  bohrLowerEnergy: "E(n_L) + C (eV)",
  bohrPhotonEnergy: "Photon energy hf = E(n_U) − E(n_L) (eV)",
  bohrWavelength: "Wavelength λ = hc/(hf) (nm)",
  bohrFrequency: "Frequency f (Hz)",
  bohrSeriesLimit: "Series limit for n_L (nm)",
  bohrBinding: "Ionization energy from n_L (eV)",
  bohrReduced: "m_r/m_e",
  bohrRelative: "λ / λ for ordinary hydrogen, same transition",
  bohrLevelsFormula: String.raw`$hf = E_U - E_L$ (39.5), $E_n = -hcR_M Z^2/n^2 + C$ with $R_M = (m_r/m_e)R_\infty$ (39.14, 39.15, 39.17). The constant $C$ cancels in every difference.`,
  bohrOrbitsSvg: "Bohr orbits and standing wave",
  bohrOrbitsDesc:
    "Left: the orbits n = 1 to n_U to scale, the upper and lower orbits marked and a standing de Broglie wave with n_U wavelengths along the upper orbit. Right: bars for r_n/r(n_U), growing as n², and |E_n|/|E_1|, falling as 1/n².",
  bohrBarsTitle: "r_n/r(n_U) and |E_n|/|E_1|",
  bohrRadius: "Separation r(n_U) (nm)",
  bohrSpeed: "v/c = Zα/n_U",
  bohrAngular: "L/ħ",
  bohrKinetic: "K = −E (eV), zero at infinity",
  bohrPotential: "U = 2E (eV), zero at infinity",
  bohrShifted: "E(n_U) + C (eV)",
  bohrDeBroglie: "de Broglie wavelength 2πr/n_U (nm)",
  bohrRadiusRatio: "r(n_U)/r(n_L)",
  bohrEnergyRatio: "E(n_U)/E(n_L), zero at infinity",
  bohrOrbitsFormula: String.raw`$r_n = n^2 a_0 (m_e/m_r)/Z$ (39.8, 39.11), $v_n = Z\alpha c/n$ (39.9), $L = n\hbar$ (39.6), $2\pi r_n = n\lambda$. $K_n = -E_n$ and $U_n = 2E_n$ (39.12–39.14) hold only with $E = 0$ at infinite separation; with the energy-zero constant $C$, $K_n = C - E_n'$ where $E_n' = E_n + C$.`,
  bohrCorrSvg: "Photon frequency against classical orbital frequency",
  bohrCorrDesc:
    "The ratio f(n + k → n)/(k f_rot(n)) against n on a logarithmic axis from 1 to 1000. The dashed horizontal line at 1 is the classical prediction; the solid curve is the chosen jump k, the dashed curve k = 1 for comparison, and the dot marks the chosen n.",
  bohrAxisLevel: "n (logarithmic)",
  bohrClassical: "classical: f = k f_rot",
  bohrRotation: "Orbital frequency f_rot(n) = v/(2πr) (Hz)",
  bohrPhotonFrequency: "Photon frequency f(n + k → n) (Hz)",
  bohrRatio: "f/(k f_rot)",
  bohrRatioGap: "1 − f/(k f_rot)",
  bohrAsymptote: "1 − 3/(2n), valid for k = 1",
  bohrCorrWavelength: "Photon wavelength (m)",
  bohrCorrFormula: String.raw`$f_\text{rot}(n) = v_n/(2\pi r_n) = 2|E_n|/(hn)$ and $f(n+k\to n) = (E_{n+k}-E_n)/h$, so $f/(k f_\text{rot}) = n^3\left[1/n^2 - 1/(n+k)^2\right]/(2k) \to 1$. For $k = 1$: $n(2n+1)/[2(n+1)^2] \approx 1 - 3/(2n)$ (Challenge 39.87).`,

  popTitle: "Populations: thermal equilibrium and four-level laser",
  popNote:
    "Thermal view: Boltzmann distribution per state, multiplied by the degeneracy for a whole level. Laser view: a simplified rate model of a steady cycle below the laser threshold, without stimulated emission.",
  popViewThermal: "Thermal equilibrium",
  popViewLaser: "Four-level laser",
  popGap: "Level gap ΔE (eV)",
  popTemperature: "Temperature T (K)",
  popLowerDegeneracy: "Degeneracy g₁ (lower level)",
  popUpperDegeneracy: "Degeneracy g₂ (upper level)",
  popThermalSvg: "Population ratio against kT/ΔE",
  popThermalDesc:
    "The ratio of upper to lower population against kT/ΔE on a logarithmic axis from 0.01 to 100. Solid curve per state, e^(−ΔE/kT), approaching 1 from below; dashed curve per level, (g₂/g₁) times that. The dashed horizontal line marks equal population.",
  popAxisX: "kT/ΔE (logarithmic)",
  popEqual: "equal population",
  popKT: "kT (eV)",
  popExponent: "ΔE/kT",
  popPerState: "Per state n₂/n₁ = e^(−ΔE/kT)",
  popPerLevel: "Per level N₂/N₁ = (g₂/g₁) e^(−ΔE/kT)",
  popUpperShare: "Share in the upper level N₂/(N₁ + N₂)",
  popNet: "Relative net absorption, 1 − n₂/n₁",
  popOutside: "Outside the plotted kT/ΔE range",
  popYes: "yes",
  popNo: "no",
  popThermalFormula: String.raw`Per state $n_2/n_1 = e^{-\Delta E/kT}$ (39.18), per level $N_2/N_1 = (g_2/g_1)\,e^{-\Delta E/kT}$. For $\Delta E > 0$ and every finite $T > 0$ the per-state ratio is below 1: thermal equilibrium never gives a population inversion. Net absorption is proportional to $n_1 - n_2$ (equal Einstein coefficients per state, beyond the book).`,
  popGround: "Ground",
  popE1: "E₁",
  popE2: "E₂",
  popE3: "E₃",
  popPump: "Pump rate W per ground-state atom (s⁻¹)",
  popUpperLifetime: "Lifetime τ₂ of E₂ (s)",
  popLowerLifetime: "Lifetime τ₁ of E₁ (s)",
  popLaserSvg: "Four-level scheme with populations",
  popLaserDesc:
    "Four levels: ground state, E₁, E₂ and E₃. Arrows show pumping from ground to E₃, decay E₃ → E₂, the laser transition E₂ → E₁ and decay E₁ → ground. The bars on the right show each level's share of the atoms on a logarithmic scale from 10⁻¹⁰ to 1.",
  popBarScale: "share (log, 10⁻¹⁰–1)",
  popTopLifetime: "Lifetime τ₃ of E₃, fixed (s)",
  popFraction: "Share in {level}",
  popInversion: "(N₂ − N₁)/N",
  popInverted: "Population inversion of E₂ over E₁",
  popFlux: "Flux through the cycle Φ/N (s⁻¹)",
  popLaserFormula: String.raw`Steady cycle ground → $E_3$ → $E_2$ → $E_1$ → ground with pump rate $W$ and lifetimes $\tau_3, \tau_2, \tau_1$: the same flux $\Phi$ passes every step, so $N_0 = \Phi/W$ and $N_i = \Phi\tau_i$. Hence $N_2 > N_1$ exactly when $\tau_2 > \tau_1$, whatever $W$ (equal degeneracy, below threshold; simplified model beyond the book).`,

  colTitle: "Collisions: Rutherford scattering and collision excitation",
  colNote:
    "Rutherford: classical orbits of an alpha particle in the nucleus's Coulomb field, nucleus fixed. Collision excitation: non-relativistic collision with a hydrogen atom at rest in its ground state.",
  colViewRutherford: "Rutherford scattering",
  colViewThreshold: "Collision excitation",
  colNucleus: "Nucleus",
  colAlphaKinetic: "Alpha-particle kinetic energy K (MeV)",
  colImpact: "Impact parameter b (fm)",
  colRutherfordSvg: "Alpha-particle orbits around a nucleus",
  colRutherfordDesc:
    "The nucleus right of centre, drawn with radius 1.2A^(1/3) fm. Alpha particles come in from the left. Grey orbits for impact parameters d/4, d/2, d and 2d; the highlighted orbit has the chosen impact parameter b. The dashed circle has radius d, the head-on turning distance.",
  colScale: "10 fm",
  colHeadOnCircle: "r = d",
  colHeadOn: "Head-on turning distance d = 2Zke²/K (fm)",
  colHeadOnRecoil: "d with recoil, K → KM/(m + M) (fm)",
  colRadius: "Nuclear radius R = 1.2A^(1/3) (fm)",
  colAngle: "Scattering angle θ (°)",
  colClosest: "Closest approach r_min (fm)",
  colClosestRatio: "r_min/R",
  colRecoil: "Nuclear recoil energy if it takes all the momentum (MeV)",
  colRecoilShare: "Recoil energy as a percentage of K",
  colRutherfordFormula: String.raw`Head-on: $K = 2Zke^2/d$, so $d = 2Zke^2/K$ with $ke^2 = 1.440$ MeV fm (Example 39.4, fixed nucleus). With impact parameter $b$: $\tan(\theta/2) = d/(2b)$ and $r_\text{min} = \tfrac{d}{2}\left(1+\sqrt{1+(2b/d)^2}\right)$. $R \approx 1.2A^{1/3}$ fm. With recoil, $K$ is replaced by $KM/(m+M)$.`,
  colElectron: "Electron",
  colHydrogen: "Hydrogen atom",
  colAlpha: "Alpha particle",
  colProjectile: "Projectile",
  colLabKinetic: "Projectile kinetic energy K in the lab (eV)",
  colThresholdSvg: "Hydrogen levels and available energy",
  colThresholdDesc:
    "Hydrogen levels n = 1 to 8 measured from the ground level, the continuum shaded above 13.6 eV. Levels the collision can reach are marked. The filled bar shows the energy that can become excitation energy, the dashed bar the projectile's lab kinetic energy.",
  colBarAvailable: "K_rel",
  colBarLab: "K",
  colFactor: "1 + m/M",
  colAvailable: "Available K_rel = KM/(m + M) (eV)",
  colCentreOfMass: "Locked in centre-of-mass motion (eV)",
  colHighest: "Highest reachable level",
  colIonizes: "ionization possible",
  colNone: "no excitation",
  colThreshold: "Threshold for n = 2: (1 + m/M)·10.2 eV (eV)",
  colSpeed: "Projectile speed (m/s)",
  colThresholdSpeed: "Minimum speed for n = 2 (m/s)",
  colLines: "Possible emission lines afterwards (nm)",
  colThresholdFormula: String.raw`Momentum is conserved, so the centre-of-mass motion keeps $Km/(m+M)$; at most $K_\text{rel} = KM/(m+M)$ can become excitation energy. The threshold is $K_\text{min} = (1+m/M)\,\Delta E$ (non-relativistic, target at rest). Electron on a hydrogen atom: $1+m/M = 1.000544$; hydrogen atom on hydrogen atom: $2$.`,
};
